import type { Point } from './viewport';

export type AxisAlignedBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type HitTarget = {
  id: string;
  z: number;
  bounds: AxisAlignedBounds;
  locked?: boolean | null;
  hidden?: boolean | null;
};

/** When z ties, higher array index wins (stable, documented tie-break). */
export function pickTopmostAtPoint(
  targets: readonly HitTarget[],
  point: Point,
  options?: { includeLocked?: boolean; includeHidden?: boolean },
): string | null {
  const includeLocked = options?.includeLocked ?? false;
  const includeHidden = options?.includeHidden ?? false;

  const hits = targets
    .map((target, index) => ({ target, index }))
    .filter(({ target }) => {
      if (!includeLocked && target.locked === true) return false;
      if (!includeHidden && target.hidden === true) return false;
      return pointInBounds(point, target.bounds);
    });

  if (hits.length === 0) {
    return null;
  }

  hits.sort((a, b) => {
    if (b.target.z !== a.target.z) {
      return b.target.z - a.target.z;
    }
    return b.index - a.index;
  });

  return hits[0]?.target.id ?? null;
}

export function pointInBounds(point: Point, bounds: AxisAlignedBounds): boolean {
  return (
    point.x >= bounds.x &&
    point.x <= bounds.x + bounds.width &&
    point.y >= bounds.y &&
    point.y <= bounds.y + bounds.height
  );
}

export function combinedAxisAlignedBounds(
  targets: readonly HitTarget[],
  ids: readonly string[],
): AxisAlignedBounds | null {
  const selected = targets.filter((target) => ids.includes(target.id));
  if (selected.length === 0) {
    return null;
  }

  const xs = selected.map((t) => t.bounds.x);
  const ys = selected.map((t) => t.bounds.y);
  const rights = selected.map((t) => t.bounds.x + t.bounds.width);
  const bottoms = selected.map((t) => t.bounds.y + t.bounds.height);

  const x = Math.min(...xs);
  const y = Math.min(...ys);
  const right = Math.max(...rights);
  const bottom = Math.max(...bottoms);

  return { x, y, width: right - x, height: bottom - y };
}
